import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, NgZone, OnDestroy, inject } from '@angular/core';

export type StoppedRecordingEvent = {
  /** Whether this recording expired  */
  expired: boolean;
  duration?: number;
};

@Component({
  selector: 'app-voice-record-button',
  templateUrl: './voice-record-button.component.html',
  styleUrls: ['./voice-record-button.component.scss'],
  standalone: false,
})
export class VoiceRecordButtonComponent implements OnDestroy {
  private zone = inject(NgZone);

  @ViewChild('button', { static: true }) button!: ElementRef<HTMLButtonElement>;

  @Input() maximumTime = 10_000; // ms
  @Input() isCountDownEnabled = false;
  @Input() disabled = false;

  /**
   * Emits when the user has signalled recording to start.
   */
  @Output() started = new EventEmitter();
  /**
   * Emits when the user has signalled recording to stop.
   */
  @Output() stopped = new EventEmitter<StoppedRecordingEvent>();

  public active = false;

  private readonly DOUBLE_TAP_GUARD_TIME = 500; // ms
  private pressStartTime: number | undefined;
  private expireTimeout: any;
  private doubleTapGuardTimeout: any;
  private doubleTapGuardActive = false;

  public size = 72; // px
  public strokeWidth = 4;
  public get halfsize() {
    return this.size / 2;
  }
  public get radius() {
    return this.halfsize - this.strokeWidth;
  }
  public get circumference() {
    return this.radius * 2 * Math.PI;
  }
  public get strokeDasharray() {
    return `${this.circumference} ${this.circumference}`;
  }
  public get offset() {
    return this.active ? 0 : this.circumference;
  }

  /**
   * Handle a click event by starting or stopping recording, and by briefly disabling the button.
   * @param event The click event.
   */
  public onClick(event: MouseEvent | TouchEvent): void {
    if (this.disabled || this.doubleTapGuardActive) {
      return;
    }

    this.temporarilyDisableButton();

    if (event.type === 'touchstart') {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!this.active) {
      this.startRecording();
    } else {
      this.stopRecording();
    }
  }

  /**
   * Temporarily disable the button to prevent quick double-presses.
   */
  private temporarilyDisableButton(): void {
    this.doubleTapGuardActive = true;
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.doubleTapGuardActive = false;
      }, this.DOUBLE_TAP_GUARD_TIME);
    });
  }

  /**
   * Signal the audio recording to start.
   */
  private startRecording(): void {
    if (this.isCountDownEnabled) {
      this.startCountdown();
    }
    this.started.emit();
  }

  /**
   * Start the countdown and the ring animation.
   */
  private startCountdown(): void {
    this.active = true;
    this.pressStartTime = Date.now();
    this.zone.runOutsideAngular(() => {
      this.expireTimeout = setTimeout(() => {
        this.zone.run(() => {
          this.stopRecording(true);
        });
      }, this.maximumTime);
    });
  }

  /**
   * Signal the audio recording to stop.
   */
  public stopRecording(expired = false): void {
    if (this.isCountDownEnabled) {
      const duration = this.stopCountdown();
      this.stopped.emit({ expired, duration });
    } else {
      this.stopped.emit({ expired });
    }
  }

  /**
   * Stop the countdown and the ring animation.
   * @returns The amount of time in milliseconds the button was held.
   */
  private stopCountdown(): number {
    this.active = false;
    clearTimeout(this.expireTimeout);
    const pressReleaseTime = Date.now();
    const heldDuration = pressReleaseTime - (this.pressStartTime ?? 0);
    this.pressStartTime = undefined;
    this.expireTimeout = undefined;
    return heldDuration;
  }

  ngOnDestroy() {
    clearTimeout(this.expireTimeout);
    clearTimeout(this.doubleTapGuardTimeout);
  }
}
